import Ionicons from "@expo/vector-icons/Ionicons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { reflectionsApi } from "@/api";
import { ThemedText } from "@/components/themed-text";
import { DateField } from "@/components/ui/date-field";
import { Pager, usePagedItems } from "@/components/ui/pager";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import {
  reflectionDateTime,
  reflectionFeelingLabel,
  reflectionOutcomeLabel,
  reflectionSubjectLabel,
} from "@/lib/reflection-format";
import type {
  Reflection,
  ReflectionHistoryQuery,
} from "@/types";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Rolling windows back from now, as `from` bounds on the query. Stored as a
 * key rather than a computed date so the fetch effect doesn't refire every
 * render, and so "last 30 days" still means the last 30 days tomorrow.
 */
const DATE_RANGES = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "3m", days: 90 },
  { key: "12m", days: 365 },
] as const;

/**
 * Reflections are never purged (see reflection-retention.service.ts), so a
 * journal can run back years. The rolling presets only reach twelve months —
 * "custom" is how someone gets to a specific week two years ago.
 */
const CUSTOM_RANGE = "custom";

type DateRangeKey = (typeof DATE_RANGES)[number]["key"] | typeof CUSTOM_RANGE;

function rangeStart(key: DateRangeKey): string | undefined {
  const days = DATE_RANGES.find((range) => range.key === key)?.days;
  if (!days) return undefined;
  const from = new Date();
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);
  return from.toISOString();
}

/** Local "YYYY-MM-DD" → the first instant of that day, as ISO. */
function startOfDayIso(value: string): string | undefined {
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Local "YYYY-MM-DD" → the last instant of that day, so `to` is inclusive. */
function endOfDayIso(value: string): string | undefined {
  const d = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

type Filters = {
  range: DateRangeKey | null;
  /** Both local "YYYY-MM-DD", and only meaningful while `range` is custom. */
  customFrom: string | null;
  customTo: string | null;
  q: string;
};

const NO_FILTERS: Filters = {
  range: null,
  customFrom: null,
  customTo: null,
  q: "",
};

function hasFilters(filters: Filters): boolean {
  return filters.range !== null || filters.q.trim() !== "";
}

/**
 * The custom bounds, smallest first. Someone picking the later date first
 * would otherwise ask for an impossible window and get an empty list back with
 * no clue why, so the two are simply read in the order that makes sense.
 */
function orderedCustomRange(filters: Filters): {
  from: string | null;
  to: string | null;
} {
  const { customFrom, customTo } = filters;
  if (customFrom && customTo && customFrom > customTo) {
    return { from: customTo, to: customFrom };
  }
  return { from: customFrom, to: customTo };
}

function toQuery(filters: Filters): ReflectionHistoryQuery {
  const query: ReflectionHistoryQuery = { limit: PAGE_SIZE };
  if (filters.range === CUSTOM_RANGE) {
    // Either end may be left empty: "since March" and "up to March" are both
    // useful, so an open bound is a filter rather than an unfinished one.
    const { from, to } = orderedCustomRange(filters);
    if (from) query.from = startOfDayIso(from);
    if (to) query.to = endOfDayIso(to);
  } else if (filters.range) {
    // Left open at the top end: "the last 30 days" runs up to right now.
    const from = rangeStart(filters.range);
    if (from) query.from = from;
  }
  const term = filters.q.trim();
  if (term) query.q = term;
  return query;
}

/**
 * The single browsable list of someone's reflections.
 *
 * Reflections are kept until the person deletes them, so this can't just render
 * everything: it pages with the server's opaque cursor and only ever holds the
 * pages actually scrolled to. Filtering happens server-side for the same reason
 * — filtering a client-side array would silently only search the pages already
 * loaded, which looks like missing data rather than a missing page.
 *
 * The API also supports field filters plus an arbitrary from/to window; a set of
 * rolling date ranges and note search are surfaced here to keep the control
 * strip short.
 */
export function ReflectionHistoryList({
  onOpen,
  refreshKey = 0,
}: {
  onOpen: (reflection: Reflection) => void;
  /** Bump to refetch from the first page — e.g. after logging or deleting. */
  refreshKey?: number;
}) {
  useTranslation();
  const c = useTheme();

  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [items, setItems] = useState<Reflection[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);

  // Only the newest request may write to state. Without this, a slow first-page
  // response can land after a later one and repopulate a filter the user has
  // already changed.
  const requestRef = useRef(0);

  // `items` holds every server page fetched so far; the reader sees five of
  // them at a time. The server page stays larger than the display page, so
  // stepping through the pager usually costs no round trip at all.
  const {
    page,
    totalPages,
    visible,
    setPage,
  } = usePagedItems(items);

  const fetchPage = useCallback(
    async (next: Filters, after: string | null) => {
      const request = ++requestRef.current;
      if (after) setLoadingMore(true);
      else setLoading(true);
      setFailed(false);
      try {
        const fetched = await reflectionsApi.history({
          ...toQuery(next),
          ...(after ? { cursor: after } : {}),
        });
        if (request !== requestRef.current) return;
        setItems((current) =>
          after ? [...current, ...fetched.items] : fetched.items,
        );
        setCursor(fetched.nextCursor);
        // A new filter or search replaces the list, so start reading at the top.
        if (!after) setPage(1);
      } catch {
        if (request !== requestRef.current) return;
        setFailed(true);
        if (!after) setItems([]);
      } finally {
        if (request === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [setPage],
  );

  // Debounce only the typed term; chip taps should feel immediate, so they
  // change `filters` directly and land in this same effect without the wait.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) =>
        current.q === searchInput ? current : { ...current, q: searchInput },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    void (async () => {
      // Yield first so the loading flag lands in its own microtask rather than
      // synchronously inside the effect body, which would cascade a render.
      // Stale responses are already handled by `requestRef`.
      await Promise.resolve();
      await fetchPage(filters, null);
    })();
  }, [fetchPage, filters, refreshKey]);

  const filtered = hasFilters(filters);
  // Nothing can be logged in the future, so both pickers stop at today. Held in
  // a ref-like memo so the Date identity doesn't change on every render.
  const today = useMemo(() => new Date(), []);

  const clearAll = () => {
    setSearchInput("");
    setFilters(NO_FILTERS);
  };

  return (
    <View style={styles.wrap}>
      <View
        style={[
          styles.searchRow,
          { backgroundColor: c.surfaceAlt, borderColor: c.border },
        ]}
      >
        <Ionicons name="search" size={17} color={c.textMuted} />
        <TextInput
          value={searchInput}
          onChangeText={setSearchInput}
          placeholder={i18n.t("growth.dashboard.searchPlaceholder")}
          placeholderTextColor={c.textMuted}
          style={[styles.searchInput, { color: c.text }]}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
        />
        {searchInput ? (
          <Pressable
            onPress={() => setSearchInput("")}
            hitSlop={10}
            accessibilityRole="button"
            accessibilityLabel={i18n.t("growth.dashboard.clearSearchA11y")}
          >
            <Ionicons name="close-circle" size={17} color={c.textMuted} />
          </Pressable>
        ) : null}
      </View>

      <FilterRow
        label={i18n.t("growth.dashboard.filterDateRange")}
        options={[
          ...DATE_RANGES.map((range) => ({
            key: range.key,
            label: i18n.t(`growth.dashboard.historyRange.${range.key}`),
          })),
          {
            key: CUSTOM_RANGE,
            label: i18n.t("growth.dashboard.historyRange.custom"),
          },
        ]}
        selected={filters.range}
        onSelect={(key) =>
          setFilters((current) => ({
            ...current,
            range: key as DateRangeKey | null,
            // Leaving custom drops the dates with it, so returning to it later
            // starts clean rather than silently reapplying an old window.
            ...(key === CUSTOM_RANGE
              ? {}
              : { customFrom: null, customTo: null }),
          }))
        }
      />

      {filters.range === CUSTOM_RANGE ? (
        <View style={styles.customRange}>
          <DateField
            value={filters.customFrom}
            onChange={(next) =>
              setFilters((current) => ({ ...current, customFrom: next }))
            }
            placeholder={i18n.t("growth.dashboard.rangeFrom")}
            accessibilityLabel={i18n.t("growth.dashboard.rangeFromA11y")}
            maximumDate={today}
          />
          <ThemedText variant="caption" tone="muted">
            →
          </ThemedText>
          <DateField
            value={filters.customTo}
            onChange={(next) =>
              setFilters((current) => ({ ...current, customTo: next }))
            }
            placeholder={i18n.t("growth.dashboard.rangeTo")}
            accessibilityLabel={i18n.t("growth.dashboard.rangeToA11y")}
            maximumDate={today}
          />
        </View>
      ) : null}

      {loading ? (
        <View style={styles.loading}>
          <ActivityIndicator color={c.tint} />
        </View>
      ) : failed ? (
        <View style={styles.empty}>
          <ThemedText variant="caption" tone="muted" center>
            {i18n.t("growth.dashboard.historyError")}
          </ThemedText>
          <Pressable
            accessibilityRole="button"
            onPress={() => void fetchPage(filters, null)}
            style={[styles.textButton, { borderColor: c.border }]}
          >
            <ThemedText variant="bodyStrong" tone="tint">
              {i18n.t("growth.dashboard.retry")}
            </ThemedText>
          </Pressable>
        </View>
      ) : items.length === 0 ? (
        <View style={styles.empty}>
          <ThemedText variant="caption" tone="muted" center>
            {filtered
              ? i18n.t("growth.dashboard.historyNoMatches")
              : i18n.t("growth.dashboard.historyEmpty")}
          </ThemedText>
          {filtered ? (
            <Pressable
              accessibilityRole="button"
              onPress={clearAll}
              style={[styles.textButton, { borderColor: c.border }]}
            >
              <ThemedText variant="bodyStrong" tone="tint">
                {i18n.t("growth.dashboard.clearFilters")}
              </ThemedText>
            </Pressable>
          ) : null}
        </View>
      ) : (
        <View style={styles.list}>
          {visible.map((reflection) => (
            <ReflectionRow
              key={reflection.id}
              reflection={reflection}
              onPress={() => onOpen(reflection)}
            />
          ))}

          <Pager
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            // A cursor means the journal continues past what has been fetched,
            // so the pager keeps its next arrow and shows a trailing "…".
            hasMore={cursor !== null}
            busy={loadingMore}
            onNextBeyondEnd={() => {
              if (!cursor) return;
              // Step onto the new page once its rows land, so pressing next
              // moves the reader rather than just widening the pager.
              const next = page + 1;
              void fetchPage(filters, cursor).then(() => setPage(next));
            }}
          />

          {loadingMore ? (
            <ActivityIndicator color={c.tint} style={styles.endNote} />
          ) : !cursor && totalPages > 1 ? (
            <ThemedText variant="caption" tone="muted" center style={styles.endNote}>
              {i18n.t("growth.dashboard.historyEnd")}
            </ThemedText>
          ) : null}
        </View>
      )}
    </View>
  );
}

/** One horizontally scrolling axis of chips, with a leading "All". */
function FilterRow({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: { key: string; label: string }[];
  selected: string | null;
  onSelect: (key: string | null) => void;
}) {
  const c = useTheme();
  return (
    <View style={styles.filterRow}>
      <ThemedText variant="caption" tone="muted" style={styles.filterLabel}>
        {label}
      </ThemedText>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.chips}
      >
        {[{ key: "", label: i18n.t("growth.dashboard.filterAll") }, ...options].map(
          (option) => {
            const key = option.key || null;
            const active = selected === key;
            return (
              <Pressable
                key={option.key || "all"}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                // Tapping the active chip clears it — one less trip to "All".
                onPress={() => onSelect(active ? null : key)}
                style={[
                  styles.chip,
                  {
                    backgroundColor: active ? c.tint : c.surfaceAlt,
                    borderColor: active ? c.tint : c.border,
                  },
                ]}
              >
                <ThemedText
                  variant="caption"
                  tone={active ? "onTint" : "secondary"}
                  numberOfLines={1}
                  maxFontSizeMultiplier={1.3}
                >
                  {option.label}
                </ThemedText>
              </Pressable>
            );
          },
        )}
      </ScrollView>
    </View>
  );
}

function ReflectionRow({
  reflection,
  onPress,
}: {
  reflection: Reflection;
  onPress: () => void;
}) {
  const c = useTheme();
  const outcome = reflection.outcome
    ? reflectionOutcomeLabel(reflection.outcome)
    : undefined;
  const subject = reflection.subject
    ? reflectionSubjectLabel(reflection.subject)
    : undefined;
  const title = outcome ?? subject ?? i18n.t("growth.reflection");
  const context = [
    subject,
    reflection.feeling ? reflectionFeelingLabel(reflection.feeling) : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={i18n.t("growth.dashboard.openReflectionA11y", {
        source: subject ?? "",
        outcome: title,
      })}
      onPress={onPress}
      style={[
        styles.row,
        { backgroundColor: c.surfaceAlt, borderColor: c.border },
      ]}
    >
      <View style={[styles.dot, { backgroundColor: c.tint }]} />
      <View style={styles.rowCopy}>
        <ThemedText variant="bodyStrong" numberOfLines={2}>
          {title}
        </ThemedText>
        {context ? (
          <ThemedText variant="caption" tone="secondary" numberOfLines={2}>
            {context}
          </ThemedText>
        ) : null}
        <ThemedText variant="caption" tone="muted" numberOfLines={1}>
          {reflectionDateTime(reflection.createdAt)}
        </ThemedText>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  customRange: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: Spacing.sm,
  },
  wrap: { gap: Spacing.md },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: Radius.lg,
  },
  searchInput: { flex: 1, paddingVertical: 2, fontSize: 15 },
  filterRow: { gap: Spacing.xs },
  filterLabel: { textTransform: "uppercase", letterSpacing: 0.6 },
  chips: { gap: Spacing.xs, paddingRight: Spacing.md },
  chip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.pill,
    borderWidth: StyleSheet.hairlineWidth,
  },
  list: { gap: Spacing.sm },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.sm,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  dot: { width: 8, height: 8, borderRadius: 4, marginTop: 6 },
  rowCopy: { flex: 1, gap: 2 },
  loading: { paddingVertical: Spacing.xl, alignItems: "center" },
  empty: { paddingVertical: Spacing.lg, gap: Spacing.sm, alignItems: "center" },
  textButton: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.pill,
    borderWidth: StyleSheet.hairlineWidth,
  },
  loadMore: {
    marginTop: Spacing.xs,
    paddingVertical: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
  },
  endNote: { paddingTop: Spacing.xs },
});
